/*
 * Copyright (c) 2020.  Rikesh Lal Shrestha
 * All Rights Reserved
 */

import { FILE_UPLOAD_CONFIG } from '../constants/appConfig';

export const downScaleImagesByResolution = (recievedFile) =>
  new Promise((resolve, reject) => {
    let height = FILE_UPLOAD_CONFIG.acceptWidth;
    let width = FILE_UPLOAD_CONFIG.acceptHeight;

    let fileName = recievedFile.name.split('.')[0];
    let fileExtension = recievedFile.name.split('.').pop().toLowerCase();

    if (fileExtension === 'heic' || fileExtension === 'heif') {
      resolve({ file: recievedFile, ratio: 1 });
      return;
    }

    let img = new Image();
    img.src = URL.createObjectURL(recievedFile);

    img.onload = function () {
      try {
        if (img.width <= width && img.height <= height) {
          resolve({ file: recievedFile, ratio: 1 });
        } else {
          let mainRation = 0;
          let widthRatio = width / img.width;
          let heightRatio = height / img.height;
          if (widthRatio < heightRatio) {
            mainRation = widthRatio;
          } else {
            mainRation = heightRatio;
          }

          if (img.height > img.width) {
            width = Math.floor(height * (img.width / img.height));
          } else {
            height = Math.floor(width * (img.height / img.width));
          }

          let resizingCanvas = document.createElement('canvas');
          let resizingCanvasContext = resizingCanvas.getContext('2d');
          resizingCanvas.width = img.width;
          resizingCanvas.height = img.height;
          resizingCanvasContext.drawImage(img, 0, 0, resizingCanvas.width, resizingCanvas.height);

          let curImageDimensions = {
            width: Math.floor(img.width),
            height: Math.floor(img.height),
          };
          let halfImageDimensions = {
            width: null,
            height: null,
          };

          halfImageDimensions.width = Math.floor(curImageDimensions.width * mainRation);
          halfImageDimensions.height = Math.floor(curImageDimensions.height * mainRation);

          resizingCanvasContext.drawImage(
            resizingCanvas,
            0,
            0,
            curImageDimensions.width,
            curImageDimensions.height,
            0,
            0,
            halfImageDimensions.width,
            halfImageDimensions.height
          );

          curImageDimensions.width = halfImageDimensions.width;
          curImageDimensions.height = halfImageDimensions.height;

          let outputCanvas = document.createElement('canvas');
          let outputCanvasContext = outputCanvas.getContext('2d');

          outputCanvas.width = width;
          outputCanvas.height = height;
          outputCanvasContext.drawImage(
            resizingCanvas,
            0,
            0,
            curImageDimensions.width,
            curImageDimensions.height,
            0,
            0,
            width,
            height
          );

          outputCanvas.toBlob(
            async function (blob) {
              let f2 = new File([blob], fileName + '.jpeg');
              resolve({ file: f2, ratio: mainRation });
            },
            'image/jpeg',
            1 // quality
          );
        }
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = function () {
      resolve({ file: recievedFile, ratio: 1 });
    };
  });

export const downScaleImagesByQuality = (recievedFile, downScaleCount) =>
  new Promise((resolve, reject) => {
    if (recievedFile.size <= FILE_UPLOAD_CONFIG.acceptThresholdSize) {
      resolve(recievedFile);
    }

    let fileName = recievedFile.name.split('.')[0];
    let img = new Image();
    img.src = URL.createObjectURL(recievedFile);

    img.onload = function () {
      let canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        async function (blob) {
          let f2 = new File([blob], fileName + downScaleCount + '.jpeg');
          resolve(f2);
        },
        'image/jpeg',
        0.9 - downScaleCount / 10 // quality
      );
    };
  });

export const checkIfDocumentCanBeViewed = (extension) =>
  extension === 'pdf' ||
  extension === 'png' ||
  extension === 'jpg' ||
  extension === 'jpeg' ||
  extension === 'heic' ||
  extension === 'heif' ||
  extension === null;

export const DOCUMENT_SIDEBAR_PREVIEW_TARGET_ID = 'sidebar-document-target';

export const DOCUMENT_SIDEBAR_PREVIEW_HEIGHT = '38vh';

export const DOCUMENT_PREVIEW_VIEW = {
  SIDEBAR: 'sidebar',
};

export const clearDocumentSidebarPreview = () => {
  const documentTarget = document.getElementById(DOCUMENT_SIDEBAR_PREVIEW_TARGET_ID);

  if (documentTarget) {
    documentTarget.innerHTML = '';
  }
};

export const PDF_EXTENSION = 'pdf';
export const PDF_MIME_TYPE = 'application/pdf';

const PDF_FILE_SIGNATURE = '%PDF-';

export const getFileExtension = (fileName) => fileName?.split('.')?.pop()?.toLowerCase();

const IMAGE_DOCUMENT_NAME = /\.(?:png|jpe?g|gif|heic|heif)$/i;

export const isPdfDocument = (documentItem) =>
  documentItem?.extension?.toLowerCase() === PDF_EXTENSION &&
  !IMAGE_DOCUMENT_NAME.test(documentItem?.name ?? '');

const MAX_TITLED_PDF_BYTES = 20 * 1024 * 1024;

const STORAGE_ID_PREFIX =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})_/i;

export const withoutStorageIdPrefix = (documentName) =>
  documentName?.replace(STORAGE_ID_PREFIX, '');

const withPdfFileName = (documentName) =>
  documentName.toLowerCase().endsWith(`.${PDF_EXTENSION}`)
    ? documentName
    : `${documentName}.${PDF_EXTENSION}`;

export const withPdfDocumentTitle = async (pdfBytes, documentName) => {
  const documentTitle = withoutStorageIdPrefix(documentName?.trim());

  if (!documentTitle || pdfBytes.byteLength > MAX_TITLED_PDF_BYTES) {
    return pdfBytes;
  }

  try {
    const { PDFDocument } = await import('pdf-lib');
    const pdfDocument = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });

    pdfDocument.setTitle(withPdfFileName(documentTitle), { showInWindowTitleBar: true });

    return await pdfDocument.save();
  } catch (error) {
    return pdfBytes;
  }
};

const readFileSignature = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String.fromCharCode.apply(null, new Uint8Array(reader.result)));
    reader.onerror = () => resolve('');
    reader.readAsArrayBuffer(file.slice(0, PDF_FILE_SIGNATURE.length));
  });

export const validatePdfFile = async (file) => {
  if (!file) {
    return 'err_pdf_only';
  }

  if (getFileExtension(file.name) !== PDF_EXTENSION) {
    return 'err_pdf_only';
  }

  if (file.type && file.type !== PDF_MIME_TYPE) {
    return 'err_pdf_only';
  }

  if (file.size > FILE_UPLOAD_CONFIG.acceptThresholdSize) {
    return 'err_document_too_large';
  }

  const fileSignature = await readFileSignature(file);

  return fileSignature === PDF_FILE_SIGNATURE ? undefined : 'err_pdf_only';
};
